// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Checkbox, Form } from "antd";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-hot-toast";

// Local Imports
import Label from "../../../../components/label/label.component";
import CustomInput from "../../../../components/custom-input/custom-input.component";
import instance from "../../../../store/axios";
import { LOADING } from "../../../../store/actions";

// Style Imports
import "./add-edit-role.scss";

interface EditRoleProps {
  onCancel: () => void;
  onConfirm: () => void;
  edit?: boolean;
  selectedRole?: any;
}

const CheckboxGroup = Checkbox.Group;

const AddEditRole: React.FC<EditRoleProps> = ({
  onCancel,
  onConfirm,
  edit = false,
  selectedRole,
}: EditRoleProps): ReactElement => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [permissionsData, setPermissionsData] = useState<any>([]);
  const [searchedPermission, setSearchedPermission] = useState<any>([]);
  const [usersData, setUsersData] = useState<any>([]);
  const [searchedUsers, setSearchedUsers] = useState<any>([]);

  useEffect(() => {
    getPermissionsData();
    getUsersData();
  }, []);

  const getPermissionsData = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/role/permission`
      );
      setPermissionsData(
        response.data.data?.map((permission: any) => {
          return {
            label: permission.description,
            value: permission.permission,
          };
        })
      );
      setSearchedPermission(
        response.data.data?.map((permission: any) => {
          return {
            label: permission.description,
            value: permission.permission,
          };
        })
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/backend-users`);
    }

    dispatch({ type: LOADING, payload: false });
  };

  const getUsersData = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin`);
      setUsersData(
        response.data?.map((user: any) => {
          return { label: user.name, value: user.id };
        })
      );
      setSearchedUsers(
        response.data?.map((user: any) => {
          return { label: user.name, value: user.id };
        })
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/backend-users`);
    }

    dispatch({ type: LOADING, payload: false });
  };

  const filterUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const users = JSON.parse(JSON.stringify(usersData));
    setSearchedUsers([
      ...users.filter((user: any) =>
        user.label?.toLowerCase().includes(event.target.value.toLowerCase())
      ),
      ...users.filter(
        (user: any) =>
          !user.label?.toLowerCase().includes(event.target.value.toLowerCase())
      ),
    ]);
  };

  const filterPermissions = (event: React.ChangeEvent<HTMLInputElement>) => {
    const permissions = JSON.parse(JSON.stringify(permissionsData));
    setSearchedPermission([
      ...permissions.filter((permission: any) =>
        permission.label
          ?.toLowerCase()
          .includes(event.target.value.toLowerCase())
      ),
      ...permissions.filter(
        (permission: any) =>
          !permission.label
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase())
      ),
    ]);
  };

  const onSubmit = async (values: any) => {
    dispatch({ type: LOADING, payload: true });

    if (selectedRole) {
      try {
        await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/role/${selectedRole?.id}`, values);
        toast.success("Role updated successfully");
        onConfirm();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
      }
    } else {
      try {
        await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/role`, values);
        toast.success("Role added successfully");
        onConfirm();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
        localStorage.setItem("star_prev_url", `/backend-users`);
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onPermissionChange = (checkedValues: any) => {
    console.log("checked = ", checkedValues);
  };

  return (
    <div className="add-edit-role-container flex flex-col w-full box-border px-4 md:px-10">
      <h3 className="text-black-500 font-semibold tracking-[0.25px] m-0 text-lg md:text-2xl">
        {edit ? `Edit role: ${selectedRole?.role}` : "Create a new role"}
      </h3>
      {!edit && (
        <p className=" text-blue-200 text-base font-medium tracking-[0.16px]">
          Please fill out the following fields to create a new role
        </p>
      )}
      <Form
        form={form}
        onFinish={onSubmit}
        scrollToFirstError={true}
        initialValues={{
          role: selectedRole?.role,
          description: selectedRole?.description,
          permissions: selectedRole?.permissions.map(
            (permission: any) => permission.permission
          ),
          admins: selectedRole?.admins.map((admin: any) => admin.id),
        }}
      >
        <div className="form flex flex-wrap justify-between border-t-[1px] border-gray-100 mt-5 pt-5">
          <div className="flex flex-col w-full md:w-1/2 box-border p-0 md:pe-5 border-0 md:border-e-[1px] border-gray-100">
            <div className="w-full flex flex-col">
              <Label text="Role name" />
              <Form.Item
                name="role"
                rules={[
                  {
                    required: true,
                    message: "Please input role name!",
                  },
                ]}
              >
                <CustomInput placeholder="Enter role name" />
              </Form.Item>
            </div>
            <div className="flex flex-col">
              <Label text="Permissions" />
              <Form.Item>
                <CustomInput
                  style={{ borderRadius: "25px" }}
                  prefix={<SearchOutlined style={{ color: "#bcb8b8" }} />}
                  placeholder="Search permissions"
                  onChange={filterPermissions}
                />
              </Form.Item>
            </div>
            <div className="flex border-[1px] border-gray-100 box-border p-3 rounded-[10px] bg-white h-[375px] overflow-auto overflow-x-hidden">
              <Form.Item
                name="permissions"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one permission!",
                  },
                ]}
              >
                <CheckboxGroup
                  options={searchedPermission}
                  onChange={onPermissionChange}
                />
              </Form.Item>
            </div>
          </div>
          <div className="flex flex-col w-full md:w-1/2 box-border px-0 md:ps-5 mt-5 md:mt-0">
            <div className="w-full flex flex-col">
              <Label text="Description" />
              <Form.Item
                name="description"
                rules={[
                  {
                    required: true,
                    message: "Please input role description!",
                  },
                ]}
              >
                <CustomInput placeholder="Enter description" />
              </Form.Item>
            </div>
            <div className="w-full flex flex-col">
              <Label text="Users" />
              <Form.Item>
                <CustomInput
                  style={{ borderRadius: "25px" }}
                  prefix={<SearchOutlined style={{ color: "#bcb8b8" }} />}
                  placeholder="Search users"
                  onChange={filterUsers}
                />
              </Form.Item>
            </div>
            <div className="flex border-[1px] border-gray-100 box-border p-3 rounded-[10px] bg-white h-[375px] overflow-auto overflow-x-hidden">
              <Form.Item
                name="admins"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one user!",
                  },
                ]}
              >
                <CheckboxGroup options={searchedUsers} />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end mt-7 md:mt-9">
          <div className="flex justify-between items-center w-full md:w-1/2">
            <button
              type="button"
              onClick={onCancel}
              className="w-[49%] border-[1px] text-blue-500 py-3 rounded-lg border-blue-500 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-[49%] border-[1px] text-white py-3 bg-blue-500 rounded-lg border-blue-500 font-semibold"
            >
              Confirm
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddEditRole;
